import React from "react";
import styled from "styled-components";
import { useTranslation } from "next-i18next";

export interface AwardItem {
  id: string;
  name: string;
  width: string;
  height: string;
  src?: string;
}

export interface AwardSectionProps {
  row1Awards?: AwardItem[];
  row2Awards?: AwardItem[];
  className?: string;
}

export const ROW1_AWARDS: AwardItem[] = [
  {
    id: "aifintech-1",
    name: "AIFINTECH Award 1",
    width: "112.06px",
    height: "120px",
    src: "/static/award/aifintech.svg",
  },
  {
    id: "aifintech-2",
    name: "AIFINTECH Award 2",
    width: "112.06px",
    height: "120px",
    src: "/static/award/datatech.svg",
  },
  {
    id: "achievement-3",
    name: "Achievement Logo 3",
    width: "211.13px",
    height: "108.75px",
    src: "/static/award/asean-ricebowl-startup.svg",
  },
  {
    id: "badge-group-4",
    name: "AI FINTECH Badge Group 4",
    width: "245.09px",
    height: "82.5px",
    src: "/static/award/insurtech.svg",
  },
];

export const ROW2_AWARDS: AwardItem[] = [
  {
    id: "award-box-1",
    name: "Award Box 1",
    width: "333.75px",
    height: "108.75px",
    src: "/static/award/global-econ-award.svg",
  },
  {
    id: "award-box-2",
    name: "Award Box 2",
    width: "233px",
    height: "80px",
    src: "/static/award/asia-star.svg",
  },
  {
    id: "award-image-3",
    name: "Award Image 3",
    width: "311px",
    height: "92px",
    src: "/static/award/aws-hackday.svg",
  },
  {
    id: "award-image-4",
    name: "Award Image 4",
    width: "207.75px",
    height: "120px",
    src: "/static/award/cnbc-indo.svg",
  },
];

const SectionContainer = styled.section`
  background: #ffffff;
  padding: 72px 0;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1440px) {
    padding: 72px 24px;
  }

  @media (max-width: 768px) {
    padding: 40px 16px;
  }
`;

const InnerContainer = styled.div`
  max-width: 1388px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    gap: 32px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 28px;
  line-height: 36px;
  font-weight: 600;
  color: #202123;
  text-align: center;
  margin: 0;
  max-width: 1000px;

  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 28px;
  }
`;

const AwardsGrid = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 40px;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    align-items: flex-start;
    align-content: center;
    gap: 16px 24px;
    width: 343px;
    height: 268.38px;
  }
`;

const Row1Container = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 64px;
  width: 100%;

  @media (max-width: 1024px) {
    gap: 40px;
  }

  @media (max-width: 768px) {
    display: contents;
  }
`;

const Row2Container = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 80px;
  width: 100%;

  @media (max-width: 1024px) {
    gap: 48px;
  }

  @media (max-width: 768px) {
    display: contents;
  }
`;

const AwardItemWrapper = styled.div<{
  $width: string;
  $height: string;
  $aspect: number;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${(props) => props.$width};
  height: ${(props) => props.$height};
  flex-shrink: 0;
  position: relative;
  box-sizing: border-box;

  @media (max-width: 768px) {
    max-width: min(100%, ${(props) => props.$width});
    height: auto;
    aspect-ratio: ${(props) => props.$aspect};

    /* 1. Brand Logo Primary */
    &.footer-logo-primary {
      order: 0;
      width: 56.03px;
      height: 60px;
      flex: none;
      flex-grow: 0;
      aspect-ratio: auto;
    }

    /* 2. Brand Logo Secondary */
    &.footer-logo-secondary {
      order: 1;
      width: 56.03px;
      height: 60px;
      flex: none;
      flex-grow: 0;
      aspect-ratio: auto;
    }

    /* 3. Partner / Media Banner */
    &.footer-partner-banner {
      order: 2;
      width: 105.56px;
      height: 54.38px;
      flex: none;
      flex-grow: 0;
      aspect-ratio: auto;
    }

    /* 4. Sub-Group: Fintech Brands */
    &.footer-fintech-group {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      gap: 3px;
      padding: 0px;

      order: 3;
      width: 122.54px;
      height: 41.25px;
      flex: none;
      flex-grow: 0;
      aspect-ratio: auto;
    }

    /* 5. Sub-Group: Awards Box 1 */
    &.footer-awards-box-1 {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 3px;
      padding: 0px;

      order: 4;
      width: 166.88px;
      height: 54.38px;
      flex: none;
      flex-grow: 0;
      aspect-ratio: auto;
    }

    /* 6. Award Vector Badge */
    &.footer-award-badge {
      order: 5;
      width: 116.44px;
      height: 40px;
      flex: none;
      flex-grow: 0;
      aspect-ratio: auto;
    }

    /* 7. Award Description Slot */
    &.footer-award-description {
      order: 6;
      width: 155.5px;
      height: 46px;
      flex: none;
      flex-grow: 0;
      aspect-ratio: auto;
    }

    /* 8. Award Featured Image */
    &.footer-award-image {
      order: 7;
      width: 103.88px;
      height: 60px;
      flex: none;
      flex-grow: 0;
      aspect-ratio: auto;
    }
  }
`;

const AwardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const LogoPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #f7f8f9;
  border: 1px dashed #d9dbde;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  color: #888d92;
  text-align: center;
  box-sizing: border-box;
  padding: 8px;
  user-select: none;
`;

const getAwardClassName = (id: string) => {
  switch (id) {
    case "aifintech-1":
      return "footer-logo-primary";
    case "aifintech-2":
      return "footer-logo-secondary";
    case "achievement-3":
      return "footer-partner-banner";
    case "badge-group-4":
      return "footer-fintech-group";
    case "award-box-1":
      return "footer-awards-box-1";
    case "award-box-2":
      return "footer-award-badge";
    case "award-image-3":
      return "footer-award-description";
    case "award-image-4":
      return "footer-award-image";
    default:
      return "";
  }
};

export const AwardSection: React.FC<AwardSectionProps> = ({
  row1Awards = ROW1_AWARDS,
  row2Awards = ROW2_AWARDS,
  className,
}) => {
  const { t } = useTranslation("icth-retail-main");
  const getAspect = (widthStr: string, heightStr: string): number => {
    const width = parseFloat(widthStr);
    const height = parseFloat(heightStr);
    return isNaN(width) || isNaN(height) || height === 0 ? 1 : width / height;
  };

  return (
    <SectionContainer className={className}>
      <InnerContainer>
        <SectionTitle>{t("award.title")}</SectionTitle>
        <AwardsGrid>
          <Row1Container>
            {row1Awards.map((award) => (
              <AwardItemWrapper
                key={award.id}
                className={getAwardClassName(award.id)}
                $width={award.width}
                $height={award.height}
                $aspect={getAspect(award.width, award.height)}
              >
                {award.src ? (
                  <AwardImage src={award.src} alt={award.name} loading="lazy" />
                ) : (
                  <LogoPlaceholder>{award.name}</LogoPlaceholder>
                )}
              </AwardItemWrapper>
            ))}
          </Row1Container>
          <Row2Container>
            {row2Awards.map((award) => (
              <AwardItemWrapper
                key={award.id}
                className={getAwardClassName(award.id)}
                $width={award.width}
                $height={award.height}
                $aspect={getAspect(award.width, award.height)}
              >
                {award.src ? (
                  <AwardImage src={award.src} alt={award.name} loading="lazy" />
                ) : (
                  <LogoPlaceholder>{award.name}</LogoPlaceholder>
                )}
              </AwardItemWrapper>
            ))}
          </Row2Container>
        </AwardsGrid>
      </InnerContainer>
    </SectionContainer>
  );
};

const val: any = () => null;
val.isRequired = () => null;

AwardSection.propTypes = {
  row1Awards: val,
  row2Awards: val,
  className: val,
};

AwardSection.defaultProps = {
  row1Awards: ROW1_AWARDS,
  row2Awards: ROW2_AWARDS,
  className: undefined,
};

export default AwardSection;
